import React, { useState } from 'react';
import axios from 'axios';
import { FaStar } from 'react-icons/fa';
import SimpleImageCropper from './SimpleImageCropper';
import './ReviewForm.scss';

const ReviewForm: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    position: '',
    review: '',
  });
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [image, setImage] = useState<string | null>(null);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        setImageToCrop(reader.result as string);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = (croppedImage: string) => {
    setImage(croppedImage);
    setShowCropper(false);
    setImageToCrop(null);
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    setImageToCrop(null);
    // Reset file input
    const fileInput = document.getElementById('image') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (rating === 0) {
      setMessage({ type: 'error', text: 'Please select a rating' });
      return;
    }

    setSubmitting(true);
    setMessage({ type: '', text: '' });

    try {
      const submitData = {
        name: formData.name,
        company: formData.company,
        position: formData.position,
        rating: rating,
        review: formData.review,
        image: image, // Already base64 from cropper
      };

      await axios.post('http://localhost:4000/api/reviews', submitData, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      setMessage({
        type: 'success',
        text: 'Thank you! Your review has been submitted and is pending approval.',
      });

      // Reset form
      setFormData({ name: '', company: '', position: '', review: '' });
      setRating(0);
      setImage(null);
      
      // Reset file input
      const fileInput = document.getElementById('image') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (error: any) {
      console.error('Review submission error:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Failed to submit review. Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="review-form">
      <div className="review-form__container">
        <h2 className="review-form__title">Share Your Experience</h2>
        <p className="review-form__subtitle">
          We'd love to hear about your experience working with us. Your feedback helps us improve
          and assists other businesses in making informed decisions.
        </p>

        {message.text && (
          <div className={`review-form__message review-form__message--${message.type}`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="review-form__form">
          <div className="review-form__row">
            <div className="review-form__group">
              <label htmlFor="name" className="review-form__label">
                Your Name <span className="review-form__required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="review-form__input"
                placeholder="John Doe"
              />
            </div>

            <div className="review-form__group">
              <label htmlFor="company" className="review-form__label">
                Company <span className="review-form__required">*</span>
              </label>
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleChange}
                required
                className="review-form__input"
                placeholder="Your Company Name"
              />
            </div>
          </div>

          <div className="review-form__row">
            <div className="review-form__group">
              <label htmlFor="position" className="review-form__label">
                Position <span className="review-form__required">*</span>
              </label>
              <input
                type="text"
                id="position"
                name="position"
                value={formData.position}
                onChange={handleChange}
                required
                className="review-form__input"
                placeholder="CEO, Manager, etc."
              />
            </div>

            <div className="review-form__group">
              <label htmlFor="image" className="review-form__label">
                Profile Image (Optional)
              </label>
              <input
                type="file"
                id="image"
                name="image"
                onChange={handleImageChange}
                accept="image/*"
                className="review-form__file"
              />
              {image && (
                <div className="review-form__image-preview">
                  <img src={image} alt="Preview" />
                  <button
                    type="button"
                    className="review-form__image-remove"
                    onClick={() => {
                      setImage(null);
                      const fileInput = document.getElementById('image') as HTMLInputElement;
                      if (fileInput) fileInput.value = '';
                    }}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          {showCropper && imageToCrop && (
            <SimpleImageCropper
              imageSrc={imageToCrop}
              onCropComplete={handleCropComplete}
              onCancel={handleCropCancel}
            />
          )}

          <div className="review-form__group">
            <label className="review-form__label">
              Rating <span className="review-form__required">*</span>
            </label>
            <div className="review-form__rating">
              {[1, 2, 3, 4, 5].map((star) => (
                <FaStar
                  key={star}
                  className={`review-form__star ${
                    star <= (hoverRating || rating) ? 'review-form__star--filled' : ''
                  }`}
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                />
              ))}
              <span className="review-form__rating-text">
                {rating > 0 ? `${rating} out of 5 stars` : 'Click to rate'}
              </span>
            </div>
          </div>

          <div className="review-form__group">
            <label htmlFor="review" className="review-form__label">
              Your Review <span className="review-form__required">*</span>
            </label>
            <textarea
              id="review"
              name="review"
              value={formData.review}
              onChange={handleChange}
              required
              rows={6}
              className="review-form__textarea"
              placeholder="Share your experience working with us..."
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="review-form__submit"
          >
            {submitting ? 'Submitting...' : 'Submit Review'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ReviewForm;

